/**
 * Compute pipeline for a tax plan.
 *
 * Two scenarios are computed in parallel:
 *   - "baseline" — no strategies applied (status-quo tax)
 *   - "planned"  — all selected strategies applied
 *
 * The flow:
 *
 *   For each Entity in plan.entities:
 *     baseline taxable income = revenue - expenses - depreciation - other
 *     planned  = baseline - sum(strategy reductions on this entity)
 *
 *     If pass-through (1120-S, 1065, sched_c):
 *       K-1 flowing to owner = planned * ownershipPct
 *       Entity itself pays no federal income tax (PTE state tax may apply)
 *
 *     If C-corp (1120):
 *       Entity pays 21% corporate tax
 *       Owner sees only dividends if/when distributed (out of scope v1)
 *
 *   For Individual (the 1040 the owner files):
 *     K-1 income from each pass-through is summed in
 *     Personal-level strategies further reduce taxable income / add credits
 *     Federal tax + state tax computed
 *
 *   Savings = baseline.totalTax - planned.totalTax
 */

import { computeFederalTax, computeCCorpTax } from "./federal.js";
import { computeStateTax, STATES } from "./states.js";
import { getStrategy } from "./strategies.js";

// Resolve a strategy's reduction-to-taxable-income contribution
export function strategyReduction(stratRef) {
  if (!stratRef || !stratRef.applied) return 0;
  const strat = getStrategy(stratRef.strategyId);
  if (!strat) return 0;

  // User-entered manual override always wins
  if (stratRef.manualReduction != null && stratRef.manualReduction !== "") {
    return Math.max(0, parseFloat(stratRef.manualReduction) || 0);
  }

  switch (strat.calcType) {
    case "reduceTaxableIncome": {
      const inputs = stratRef.inputs || {};
      // Augusta-rule special: days × rate
      if (strat.id === "augusta_rule") {
        const days = Math.min(14, parseFloat(inputs.days) || 0);
        const rate = parseFloat(inputs.dailyRate) || 0;
        return days * rate;
      }
      return Math.max(0, parseFloat(inputs.amount) || 0);
    }
    case "taxCredit":
    case "rateDelta":
    case "manual":
    default:
      return 0;
  }
}

export function strategyCredit(stratRef) {
  if (!stratRef || !stratRef.applied) return 0;
  const strat = getStrategy(stratRef.strategyId);
  if (!strat) return 0;
  if (stratRef.manualCredit != null && stratRef.manualCredit !== "") {
    return Math.max(0, parseFloat(stratRef.manualCredit) || 0);
  }
  if (strat.calcType === "taxCredit") {
    const inputs = stratRef.inputs || {};
    return Math.max(0, parseFloat(inputs.amount) || 0);
  }
  return 0;
}

export function strategyManualSavings(stratRef) {
  // For a strategy with calcType="manual", the planner can directly enter
  // an estimated dollar tax savings. Used when the strategy's economic
  // benefit isn't a simple income reduction (e.g. ILIT, QSBS).
  if (!stratRef || !stratRef.applied) return 0;
  const strat = getStrategy(stratRef.strategyId);
  if (!strat) return 0;
  if (stratRef.manualSavings != null && stratRef.manualSavings !== "") {
    return Math.max(0, parseFloat(stratRef.manualSavings) || 0);
  }
  return 0;
}

// Cash↔accrual adjustment math.
// Convention: "delta" = the amount to ADD to accrual book income to get cash
// taxable income. Tax-year balances should be 12/31 of the planning year.
// We prefer the Year-End Forecast column if entered (full-year projection),
// otherwise fall back to Current Year-to-Date balances.
//
//   accrualBookIncome
//   + (priorAR − endingAR)              // AR up → cash less than accrual
//   + (endingAP − priorAP)              // AP up → cash MORE than accrual (less expense paid)
//   + (endingAccruedLiab − priorAccruedLiab)   // accrued liab like AP
//   + (priorPrepaid − endingPrepaid)    // prepaid up → cash less (paid but not expensed)
//   + (priorContractAsset − endingContractAsset)   // contract asset like AR
//   + (endingContractLiab − priorContractLiab)     // contract liab (def. revenue) → cash MORE
//   = cashTaxableIncome
//
// where endingX = yeX (year-end forecast) if non-zero, else currentX (YTD).
export function cashAccrualDelta(ca = {}) {
  const n = (v) => parseFloat(v) || 0;
  // ending(key) picks the year-end forecast if set, otherwise the current YTD.
  const ending = (yeKey, currKey) =>
    n(ca[yeKey]) !== 0 ? n(ca[yeKey]) : n(ca[currKey]);

  const arDelta = n(ca.priorAR) - ending("yeAR", "currentAR");
  const apDelta = ending("yeAP", "currentAP") - n(ca.priorAP);
  const accDelta =
    ending("yeAccruedLiab", "currentAccruedLiab") - n(ca.priorAccruedLiab);
  const prepaidDelta = n(ca.priorPrepaid) - ending("yePrepaid", "currentPrepaid");
  const contractAssetDelta =
    n(ca.priorContractAsset) -
    ending("yeContractAsset", "currentContractAsset");
  const contractLiabDelta =
    ending("yeContractLiab", "currentContractLiab") - n(ca.priorContractLiab);

  // Partner-defined custom lines — sum into the total delta using the
  // direction toggle on each row.
  const customDelta = Array.isArray(ca.customLines)
    ? ca.customLines.reduce((sum, l) => {
        const prior = parseFloat(l.prior) || 0;
        const ye = parseFloat(l.ye) || 0;
        const curr = parseFloat(l.current) || 0;
        const endVal = ye !== 0 ? ye : curr;
        const lineDelta =
          l.direction === "liability" ? endVal - prior : prior - endVal;
        return sum + lineDelta;
      }, 0)
    : 0;

  return (
    arDelta +
    apDelta +
    accDelta +
    prepaidDelta +
    contractAssetDelta +
    contractLiabDelta +
    customDelta
  );
}

// ─── Entity computation ────────────────────────────────────────────────
export function computeEntity(entity, applyStrategies = true) {
  const revenue = +entity.revenue || 0;
  const expenses = +entity.expenses || 0;
  const depreciation = +entity.depreciation || 0;
  const otherDeductions = +entity.otherDeductions || 0;
  const otherIncome = +entity.otherIncome || 0;

  const bookAccrualIncome = revenue + otherIncome - expenses - depreciation - otherDeductions;

  // Book-to-tax addbacks: book expenses the planner is disregarding to get
  // to an adjusted accrual taxable income (the starting point Schedule L
  // would arrive at if these items were not on the income statement).
  const bt = entity.bookToTax || {};
  const bookToTaxAddback =
    (parseFloat(bt.bookDepreciation) || 0) +
    (parseFloat(bt.bookAmortization) || 0) +
    (parseFloat(bt.principalPayments) || 0) +
    (parseFloat(bt.mortgagePayments) || 0) +
    (parseFloat(bt.nonDeductibleTax) || 0) +
    (parseFloat(bt.other) || 0) +
    (Array.isArray(bt.otherItems)
      ? bt.otherItems.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0)
      : 0);

  const accrualIncome = bookAccrualIncome + bookToTaxAddback;

  // Cash/accrual adjustment — only applied if the entity files on cash for tax
  const taxMethod = entity.taxMethod || "accrual"; // "cash" | "accrual"
  const caDelta = cashAccrualDelta(entity.cashAccrual);
  // If override is set, use it; otherwise use computed delta when method is "cash"
  const caOverride =
    entity.cashAccrual?.appliedAdjustmentOverride !== "" &&
    entity.cashAccrual?.appliedAdjustmentOverride != null
      ? parseFloat(entity.cashAccrual.appliedAdjustmentOverride) || 0
      : null;
  const cashAccrualAdjustment =
    caOverride != null ? caOverride : taxMethod === "cash" ? caDelta : 0;

  const baselineIncome = accrualIncome + cashAccrualAdjustment;

  const reductions = applyStrategies
    ? (entity.strategies || []).reduce((sum, s) => sum + strategyReduction(s), 0)
    : 0;
  const credits = applyStrategies
    ? (entity.strategies || []).reduce((sum, s) => sum + strategyCredit(s), 0)
    : 0;
  const manualSavings = applyStrategies
    ? (entity.strategies || []).reduce((sum, s) => sum + strategyManualSavings(s), 0)
    : 0;

  const taxableIncome = Math.max(0, baselineIncome - reductions);

  const ownership = (parseFloat(entity.ownershipPct) || 0) / 100;
  const isPassthrough =
    entity.type === "1120S" || entity.type === "1065" || entity.type === "sched_c";
  const isCCorp = entity.type === "1120";

  // Entity-level K-1 flow-through items (separate from ordinary business income)
  const entityLTCG = parseFloat(entity.ltcg) || 0;
  const entitySTCG = parseFloat(entity.stcg) || 0;

  // Build the effective list of named pass-through credits. If the new
  // passthroughCreditItems array is populated, use it; otherwise fall back
  // to the legacy single passthroughCredits value as a single seeded item.
  const rawCreditItems = Array.isArray(entity.passthroughCreditItems)
    ? entity.passthroughCreditItems.filter(
        (i) => (parseFloat(i.amount) || 0) !== 0,
      )
    : [];
  const legacyPassthroughCredits = parseFloat(entity.passthroughCredits) || 0;
  const effectiveCreditItems =
    rawCreditItems.length > 0
      ? rawCreditItems
      : legacyPassthroughCredits !== 0
        ? [
            {
              id: "legacy",
              name: "Pass-through credits",
              amount: legacyPassthroughCredits,
            },
          ]
        : [];
  const entityPassthroughCredits = effectiveCreditItems.reduce(
    (sum, i) => sum + (parseFloat(i.amount) || 0),
    0,
  );

  let entityTax = 0;
  let ownerK1Ordinary = 0;
  let ownerK1SE = 0;
  let ownerK1LTCG = 0;
  let ownerK1STCG = 0;
  let ownerK1Credits = 0;
  let ownerK1CreditItems = [];
  // §199A wage/UBIA ceiling for this entity (only meaningful on pass-throughs).
  // = greater of (50% × W-2 wages) AND (25% × W-2 wages + 2.5% × UBIA).
  // Applied at the 1040 level when taxable income exceeds the QBI threshold.
  let ownerK1QBIWageCeiling = 0;

  if (isPassthrough) {
    ownerK1Ordinary = taxableIncome * ownership;
    ownerK1LTCG = entityLTCG * ownership;
    ownerK1STCG = entitySTCG * ownership;
    ownerK1Credits = entityPassthroughCredits * ownership;
    const entWages = parseFloat(entity.qbiWages) || 0;
    const entUBIA = parseFloat(entity.qbiUBIA) || 0;
    const entCeiling = Math.max(
      0.5 * entWages,
      0.25 * entWages + 0.025 * entUBIA,
    );
    ownerK1QBIWageCeiling = entCeiling * ownership;
    ownerK1CreditItems = effectiveCreditItems.map((it) => ({
      id: it.id,
      name: it.name || "(Unnamed credit)",
      ownerAmount: (parseFloat(it.amount) || 0) * ownership,
      sourceEntityName: entity.name,
      sourceEntityId: entity.id,
    }));
    // Sole prop and general-partner share are SE-taxable (ordinary only)
    if (entity.type === "sched_c") {
      ownerK1SE = ownerK1Ordinary;
    } else if (entity.type === "1065" && entity.generalPartner) {
      ownerK1SE = ownerK1Ordinary;
    }
  } else if (isCCorp) {
    const cTax = computeCCorpTax({
      year: entity.year || 2026,
      revenue,
      expenses,
      depreciation,
      otherDeductions,
      officerComp: 0,
    });
    entityTax = cTax.totalTax;
  }

  return {
    id: entity.id,
    name: entity.name,
    type: entity.type,
    bookAccrualIncome,
    bookToTaxAddback,
    accrualIncome,
    cashAccrualAdjustment,
    taxMethod,
    baselineIncome,
    reductions,
    credits,
    manualSavings,
    taxableIncome,
    ownership,
    ownerK1Ordinary,
    ownerK1SE,
    ownerK1LTCG,
    ownerK1STCG,
    ownerK1Credits,
    ownerK1CreditItems,
    ownerK1QBIWageCeiling,
    entityLTCG,
    entitySTCG,
    entityPassthroughCredits,
    entityTax,
    isPassthrough,
    isCCorp,
    apportionment: Array.isArray(entity.apportionment) ? entity.apportionment : [],
  };
}

// ─── Plan-level computation ────────────────────────────────────────────
export function computePlan(plan, { applyStrategies = true } = {}) {
  const year = plan.taxYear || 2026;
  const filingStatus = plan.individual?.filingStatus || "single";
  const state = plan.state || "UT";

  const entityResults = (plan.entities || []).map((e) =>
    computeEntity({ ...e, year }, applyStrategies),
  );

  // Aggregate K-1 flows to owner
  const k1Ordinary = entityResults.reduce((sum, r) => sum + r.ownerK1Ordinary, 0);
  const k1SE = entityResults.reduce((sum, r) => sum + r.ownerK1SE, 0);
  const k1LTCG = entityResults.reduce((sum, r) => sum + (r.ownerK1LTCG || 0), 0);
  const k1STCG = entityResults.reduce((sum, r) => sum + (r.ownerK1STCG || 0), 0);
  const k1Credits = entityResults.reduce((sum, r) => sum + (r.ownerK1Credits || 0), 0);
  // Flat list of named credit items from every pass-through entity, so the
  // 1040 can show "R&D credit (Acme S-Corp): $5,000" instead of one lump sum.
  const k1CreditItems = entityResults.flatMap((r) => r.ownerK1CreditItems || []);
  const entityCredits = entityResults.reduce((sum, r) => sum + r.credits, 0);
  const entityManualSavings = entityResults.reduce((sum, r) => sum + r.manualSavings, 0);
  const cCorpTax = entityResults.reduce((sum, r) => sum + r.entityTax, 0);

  // Individual 1040 inputs
  const ind = plan.individual || {};
  const personalReductions = applyStrategies
    ? (ind.strategies || []).reduce((sum, s) => sum + strategyReduction(s), 0)
    : 0;
  const personalCredits = applyStrategies
    ? (ind.strategies || []).reduce((sum, s) => sum + strategyCredit(s), 0)
    : 0;
  const personalManualSavings = applyStrategies
    ? (ind.strategies || []).reduce((sum, s) => sum + strategyManualSavings(s), 0)
    : 0;

  // QBI base = pass-through ordinary K-1
  const qbiIncome = k1Ordinary;
  // Aggregated §199A wage/UBIA ceiling across all pass-through entities,
  // apportioned to the owner. Applied at the 1040 level when taxable income
  // exceeds the QBI threshold (and the ceiling is binding).
  const qbiWageCeiling = entityResults.reduce(
    (s, r) => s + (r.ownerK1QBIWageCeiling || 0),
    0,
  );
  // Planner-controlled override for the QBI deduction itself — partner
  // judgment call when wage/UBIA limits or SSTB phase-out apply.
  const qbiOverrideAmt =
    ind.qbiOverride === "" || ind.qbiOverride == null
      ? null
      : Math.max(0, parseFloat(ind.qbiOverride) || 0);

  // NIIT exclusion — planner marks LTCG that is not subject to the 3.8% NIIT
  // (e.g. active-trade-or-business sale gain). We subtract it from investment
  // income before NIIT computes by pre-adding it as a positive adjustment.
  const ltcgExcludedFromNIIT = Math.max(
    0,
    parseFloat(ind.ltcgExcludedFromNIIT) || 0,
  );

  const fedInput = {
    year,
    filingStatus,
    wages: +ind.wages || 0,
    interest: +ind.interest || 0,
    ordinaryDividends: +ind.ordinaryDividends || 0,
    qualifiedDividends: +ind.qualifiedDividends || 0,
    // K-1 capital gains aggregate into the personal capital gains buckets
    // and are taxed at the appropriate rate (LTCG preferential, STCG ordinary).
    shortTermGains: (+ind.shortTermGains || 0) + k1STCG,
    longTermGains: (+ind.longTermGains || 0) + k1LTCG,
    businessIncome: k1Ordinary,
    seIncome: k1SE,
    qbiIncome,
    qbiWageCeiling,
    qbiOverride: qbiOverrideAmt,
    niitExclusion: ltcgExcludedFromNIIT,
    amtPreferences: parseFloat(ind.amtPreferences) || 0,
    qualifyingChildren: parseInt(ind.qualifyingChildren, 10) || 0,
    otherDependents: parseInt(ind.otherDependents, 10) || 0,
    seEarnerWages: parseFloat(ind.seEarnerWages) || 0,
    otherIncome: +ind.otherIncome || 0,
    adjustments: (+ind.adjustments || 0) + personalReductions,
    itemizedDeductions: +ind.itemizedDeductions || 0,
  };

  const fed = computeFederalTax(fedInput);

  // State taxable income — federal taxable income plus state adjustments
  const entityStateAdjustments = (plan.entities || []).reduce((sum, e) => {
    const adj = parseFloat(e.stateAdjustment) || 0;
    const ownership = (parseFloat(e.ownershipPct) || 0) / 100;
    const isPassthrough =
      e.type === "1120S" || e.type === "1065" || e.type === "sched_c";
    return sum + (isPassthrough ? adj * ownership : 0);
  }, 0);
  const individualStateAdjustment = parseFloat(ind.stateAdjustment) || 0;
  const stateTaxableIncome = Math.max(
    0,
    fed.taxableIncome + entityStateAdjustments + individualStateAdjustment,
  );

  // ────────────────────────────────────────────────────────────────────
  // Multi-state tax allocation
  //
  // For each pass-through entity that has explicit apportionment, the
  // owner's K-1 ordinary share is distributed across the apportioned
  // states. C-corps with apportionment have entity-level state tax
  // distributed similarly. Personal income (everything not from a K-1)
  // is taxed at the resident state. We sum each piece for total state tax.
  //
  // Entities with no apportionment fall back to the plan's resident state.
  //
  // This is a simplified model — it doesn't compute the credit for taxes
  // paid to non-resident states. For planning approximations across the
  // multi-state footprint, it's directionally correct.
  // ────────────────────────────────────────────────────────────────────
  const residentState = state;
  const stateTaxByState = {}; // state code -> dollar tax
  let entityStateTaxTotal = 0;

  for (const er of entityResults) {
    const apportionment =
      er.apportionment && er.apportionment.length > 0
        ? er.apportionment
        : [{ state: residentState, pct: 100 }];

    // Income to distribute: K-1 ordinary for pass-throughs, taxable income for C-corps
    const incomeToApportion = er.isPassthrough
      ? er.ownerK1Ordinary
      : er.taxableIncome;

    // Also allocate the entity's state adjustment to the same apportionment
    const entityAdj =
      (parseFloat(plan.entities.find((e) => e.id === er.id || e.name === er.name)?.stateAdjustment) ||
        0) * (er.isPassthrough ? er.ownership : 1);

    for (const row of apportionment) {
      const pct = (parseFloat(row.pct) || 0) / 100;
      const incomePortion = incomeToApportion * pct;
      const adjPortion = entityAdj * pct;
      const taxableForThisState = Math.max(0, incomePortion + adjPortion);
      const stateRes = computeStateTax({
        state: row.state,
        taxableIncome: taxableForThisState,
        capitalGains: 0,
        filingStatus,
      });
      const tax = stateRes.totalTax || 0;
      entityStateTaxTotal += tax;
      stateTaxByState[row.state] = (stateTaxByState[row.state] || 0) + tax;
    }
  }

  // Personal income that isn't from a K-1: wages + interest + dividends +
  // personal cap gains + other - adjustments - deduction - QBI
  const k1OrdinaryAndCapGains =
    k1Ordinary +
    entityResults.reduce(
      (s, r) => s + (r.ownerK1LTCG || 0) + (r.ownerK1STCG || 0),
      0,
    );
  const personalStateTaxable = Math.max(
    0,
    fed.taxableIncome - k1OrdinaryAndCapGains + individualStateAdjustment,
  );
  const personalStateRes = computeStateTax({
    state: residentState,
    taxableIncome: personalStateTaxable,
    capitalGains: fedInput.longTermGains + fedInput.shortTermGains,
    filingStatus,
  });
  const personalStateTax = personalStateRes.totalTax || 0;
  stateTaxByState[residentState] =
    (stateTaxByState[residentState] || 0) + personalStateTax;

  // Determine if any entity uses multi-state apportionment (informational)
  const isMultiState = (plan.entities || []).some(
    (e) => (e.apportionment || []).length > 0,
  );

  // Aggregate state tax — preserve the same `st` shape downstream code expects
  const totalStateTax = entityStateTaxTotal + personalStateTax;
  const st = {
    state: residentState,
    totalTax: totalStateTax,
    rate: STATES[residentState]?.topRate || 0,
    kind: isMultiState ? "multi-state" : STATES[residentState]?.kind || "graduated",
    note: STATES[residentState]?.note,
    approximate: true,
    multiState: isMultiState,
    byState: stateTaxByState,
    entityPortion: entityStateTaxTotal,
    personalPortion: personalStateTax,
  };

  // PTE credits across entities (sum of pteCreditUsed at owner level)
  const pteCredits = (plan.entities || []).reduce(
    (sum, e) => sum + (parseFloat(e.pteCreditUsed) || 0),
    0,
  );

  // K-1 pass-through credits flow to the owner's federal return
  const totalCredits = entityCredits + personalCredits + pteCredits + k1Credits;
  const incomeTax = Math.max(0, fed.totalTax - totalCredits) + st.totalTax + cCorpTax;
  // Manual savings reduce the displayed tax dollar-for-dollar (planner judgment)
  const totalTax = Math.max(0, incomeTax - entityManualSavings - personalManualSavings);

  return {
    year,
    state,
    filingStatus,
    entities: entityResults,
    k1OrdinaryTotal: k1Ordinary,
    k1SETotal: k1SE,
    k1LTCGTotal: k1LTCG,
    k1STCGTotal: k1STCG,
    k1CreditsTotal: k1Credits,
    k1CreditItems,
    federal: fed,
    stateTaxableIncome,
    stateAdjustmentsTotal: entityStateAdjustments + individualStateAdjustment,
    stateTax: st,
    cCorpTax,
    creditsApplied: totalCredits,
    pteCredits,
    manualSavingsApplied: entityManualSavings + personalManualSavings,
    incomeTax,
    totalTax,
  };
}

export function computePlanWithSavings(plan) {
  const baseline = computePlan(plan, { applyStrategies: false });
  const planned = computePlan(plan, { applyStrategies: true });
  const savings = Math.max(0, baseline.totalTax - planned.totalTax);
  const fedSavings = Math.max(
    0,
    baseline.federal.totalTax - planned.federal.totalTax,
  );
  const stateSavings = Math.max(
    0,
    baseline.stateTax.totalTax - planned.stateTax.totalTax,
  );

  // ─── Per-scenario compute ────────────────────────────────────────────
  // Each strategy can be Yes/Maybe/Out per scenario via stratRef.statusByScenario.
  // Backward-compat: if statusByScenario isn't set, fall back to the legacy
  // single `status` field — so existing plans render with all 3 scenarios
  // mirroring the old single status.
  const scenarioList =
    Array.isArray(plan.scenarios) && plan.scenarios.length > 0
      ? plan.scenarios
      : [
          { id: "s1", name: "Scenario 1" },
          { id: "s2", name: "Scenario 2" },
          { id: "s3", name: "Scenario 3" },
        ];

  const statusFor = (ref, scenarioId) => {
    const sbS = ref.statusByScenario;
    if (sbS && sbS[scenarioId]) return sbS[scenarioId];
    // Legacy: derive from single status. yes/committed/implemented → yes; else maybe.
    if (
      ref.status === "yes" ||
      ref.status === "committed" ||
      ref.status === "implemented"
    )
      return "yes";
    return "maybe";
  };

  const scenarios = scenarioList.map((sc) => {
    // Yes-only plan: strategies marked "yes" for THIS scenario
    const yesPlan = withStrategyFilter(
      plan,
      (s) => statusFor(s, sc.id) === "yes",
    );
    const yesRun = computePlan(yesPlan, { applyStrategies: true });
    // Full plan: yes + maybe (excluding "out")
    const fullPlan = withStrategyFilter(plan, (s) => {
      const st = statusFor(s, sc.id);
      return st === "yes" || st === "maybe";
    });
    const fullRun = computePlan(fullPlan, { applyStrategies: true });

    const yesOnlyTax = yesRun.totalTax;
    const fullTax = fullRun.totalTax;
    const yesOnlySav = Math.max(0, baseline.totalTax - yesOnlyTax);
    const fullSav = Math.max(0, baseline.totalTax - fullTax);
    const maybeSav = Math.max(0, fullSav - yesOnlySav);
    const yesCount = countAppliedWithStatus(plan, sc.id, "yes", statusFor);
    const maybeCount = countAppliedWithStatus(plan, sc.id, "maybe", statusFor);

    return {
      id: sc.id,
      name: sc.name,
      yesOnlyTax,
      fullTax,
      yesOnlySavings: yesOnlySav,
      fullSavings: fullSav,
      maybeSavings: maybeSav,
      yesCount,
      maybeCount,
    };
  });

  // For the headline + simple-display fallback, use the LARGEST scenario
  // (most strategies in play = biggest projection). Most planning conversations
  // pivot around the comprehensive number; staff iterate narrower scenarios.
  const primaryScenario =
    scenarios.length === 0
      ? null
      : scenarios.reduce(
          (best, cur) => (cur.fullSavings > best.fullSavings ? cur : best),
          scenarios[0],
        );

  // Backward-compat aliases — older UI code reads these single values.
  // Use the primary scenario's numbers so the legacy hero/subtitle keep working.
  const yesOnlyTotalTax = primaryScenario
    ? primaryScenario.yesOnlyTax
    : planned.totalTax;
  const yesOnlySavings = primaryScenario ? primaryScenario.yesOnlySavings : 0;
  const maybeSavings = primaryScenario ? primaryScenario.maybeSavings : 0;
  const committedSavings = yesOnlySavings;
  const tentativeSavings = maybeSavings;

  // Per-strategy marginal contribution. For each applied strategy, we
  // recompute the plan WITHOUT that strategy and take the delta vs. the
  // current planned outcome. This is the true add-this-strategy savings
  // at the margin given everything else is already applied.
  const marginalById = {};
  forEachAppliedStrategy(plan, (ref) => {
    const variantPlan = withStrategyFilter(
      plan,
      (s) => !(s === ref || s.strategyId === ref.strategyId && s.addedAt === ref.addedAt),
    );
    const variant = computePlan(variantPlan, { applyStrategies: true });
    const marginal = Math.max(0, variant.totalTax - planned.totalTax);
    // Index by strategyId — sum across multiple instances of the same
    // strategy on different hosts so the picker tally stays meaningful.
    marginalById[ref.strategyId] =
      (marginalById[ref.strategyId] || 0) + marginal;
  });

  // Safe-harbor & quarterly estimated tax (Form 2210)
  const estimatedTax = computeEstimatedTax(plan, planned.totalTax);

  // Multi-year projection (defaults to 3 years forward including current)
  const multiYear = computeMultiYear(plan);

  return {
    baseline,
    planned,
    savings,
    fedSavings,
    stateSavings,
    // Multi-scenario fields — array of scenario results (Yes-only + Yes+Maybe per scenario)
    scenarios,
    primaryScenarioId: primaryScenario?.id,
    // Single-scenario fallback fields (use primary scenario's numbers)
    yesOnlyTotalTax,
    yesOnlySavings,
    maybeSavings,
    // Safe harbor + quarterly schedule
    estimatedTax,
    // Multi-year projection
    multiYear,
    // Backward-compat aliases for existing UI code
    committedSavings,
    tentativeSavings,
    marginalById,
  };
}

// ─── Multi-year projection ─────────────────────────────────────────────
// Project N years forward (default 3, including the current plan year).
// For each year:
//   - Scale gross income inputs by (1 + incomeGrowth)^yearOffset
//   - Drop strategies whose yearsActive < current year number
//     (so a "1-year" strategy like cost seg fires Y1 only)
//   - Run baseline + planned through the existing pipeline
// Cumulative totals sum across the projection window.
function computeMultiYear(plan) {
  const yearCount = Math.max(
    1,
    Math.min(parseInt(plan.projectionYears, 10) || 3, 5),
  );
  const growth = parseFloat(plan.incomeGrowth);
  const safeGrowth = isNaN(growth) ? 0.03 : Math.max(-0.5, Math.min(growth, 1));
  const baseYear = plan.taxYear || new Date().getFullYear();
  const years = [];

  for (let i = 0; i < yearCount; i++) {
    const yearNumber = i + 1; // 1-based: 1 = current, 2 = next, 3 = year after
    const scaleFactor = Math.pow(1 + safeGrowth, i);
    const scaledPlan = scalePlanForYear(plan, scaleFactor, yearNumber);
    const baselineRun = computePlan(scaledPlan, { applyStrategies: false });
    const plannedRun = computePlan(scaledPlan, { applyStrategies: true });
    const yearSavings = Math.max(0, baselineRun.totalTax - plannedRun.totalTax);
    years.push({
      yearNumber,
      taxYear: baseYear + i,
      scaleFactor,
      baselineTax: baselineRun.totalTax,
      plannedTax: plannedRun.totalTax,
      savings: yearSavings,
    });
  }

  const cumulative = years.reduce(
    (acc, y) => ({
      baselineTax: acc.baselineTax + y.baselineTax,
      plannedTax: acc.plannedTax + y.plannedTax,
      savings: acc.savings + y.savings,
    }),
    { baselineTax: 0, plannedTax: 0, savings: 0 },
  );

  return {
    years,
    cumulative,
    incomeGrowth: safeGrowth,
    yearCount,
  };
}

// Scale income inputs by a factor and filter strategies by yearsActive.
function scalePlanForYear(plan, scaleFactor, yearNumber) {
  const scaleNum = (v) => (parseFloat(v) || 0) * scaleFactor;
  const filterStrats = (refs) =>
    (refs || []).map((s) => {
      if (!s.applied) return s;
      const ya = parseInt(s.yearsActive, 10);
      const yearsActive = isNaN(ya) ? 3 : Math.max(1, ya);
      // Strategy fires only when its remaining life covers this year
      return yearNumber <= yearsActive ? s : { ...s, applied: false };
    });

  return {
    ...plan,
    entities: (plan.entities || []).map((e) => ({
      ...e,
      revenue: scaleNum(e.revenue),
      expenses: scaleNum(e.expenses),
      depreciation: scaleNum(e.depreciation),
      strategies: filterStrats(e.strategies),
    })),
    individual: {
      ...(plan.individual || {}),
      wages: scaleNum(plan.individual?.wages),
      interest: scaleNum(plan.individual?.interest),
      ordinaryDividends: scaleNum(plan.individual?.ordinaryDividends),
      qualifiedDividends: scaleNum(plan.individual?.qualifiedDividends),
      shortTermGains: scaleNum(plan.individual?.shortTermGains),
      longTermGains: scaleNum(plan.individual?.longTermGains),
      otherIncome: scaleNum(plan.individual?.otherIncome),
      strategies: filterStrats(plan.individual?.strategies),
    },
  };
}

// ─── Safe-harbor & quarterly estimated tax ─────────────────────────────
// IRC §6654 safe harbor: pay the LESSER of
//   - 110% of prior-year total tax (100% if prior AGI ≤ $150K), OR
//   - 90% of current-year total tax
// to avoid underpayment penalty. Withholding counts as evenly paid
// across the year; estimated payments count when actually made.
function computeEstimatedTax(plan, currentYearTotalTax) {
  const ind = plan.individual || {};
  const priorTotal = Math.max(0, parseFloat(ind.priorYearTotalTax) || 0);
  const priorAGI = Math.max(0, parseFloat(ind.priorYearAGI) || 0);
  const withholding = Math.max(0, parseFloat(ind.expectedWithholding) || 0);
  const paymentsMade = Math.max(0, parseFloat(ind.estimatedPaymentsMade) || 0);
  const taxYear = plan.taxYear || new Date().getFullYear();

  // 110% rule kicks in when prior AGI > $150K (or $75K for MFS)
  const status = ind.filingStatus || "single";
  const highIncomeThreshold = status === "mfs" ? 75000 : 150000;
  const priorMultiplier = priorAGI > highIncomeThreshold ? 1.10 : 1.00;
  const priorYearSafeHarbor = priorTotal * priorMultiplier;
  const currentYearSafeHarbor = currentYearTotalTax * 0.9;

  // If planner hasn't entered prior-year facts, fall back to the current-year
  // 90% rule so we still produce useful numbers (just flag it).
  const hasPriorYear = priorTotal > 0;
  const safeHarborAmount = hasPriorYear
    ? Math.min(priorYearSafeHarbor, currentYearSafeHarbor)
    : currentYearSafeHarbor;
  const safeHarborBasis = hasPriorYear
    ? priorYearSafeHarbor < currentYearSafeHarbor
      ? `${(priorMultiplier * 100).toFixed(0)}% of prior-year tax`
      : "90% of current-year tax"
    : "90% of current-year tax (prior-year facts not entered)";

  // Net required = safe harbor minus withholding minus payments already made
  const netRequired = Math.max(0, safeHarborAmount - withholding - paymentsMade);
  const perQuarter = netRequired / 4;

  // 2025/2026 IRS due dates for individual estimated tax
  const quarterly = [
    { quarter: 1, due: new Date(taxYear, 3, 15), amount: perQuarter, label: `Q1 ${taxYear}` },
    { quarter: 2, due: new Date(taxYear, 5, 16), amount: perQuarter, label: `Q2 ${taxYear}` },
    { quarter: 3, due: new Date(taxYear, 8, 15), amount: perQuarter, label: `Q3 ${taxYear}` },
    { quarter: 4, due: new Date(taxYear + 1, 0, 15), amount: perQuarter, label: `Q4 ${taxYear}` },
  ];

  // Final balance due with the return (rough — assumes safe-harbor target met)
  const balanceDueAtFiling = Math.max(
    0,
    currentYearTotalTax - withholding - paymentsMade - netRequired,
  );

  // Penalty exposure if the planner's expected withholding + estimated payments
  // fall short of the safe harbor target.
  const projectedPaid = withholding + paymentsMade + netRequired;
  const shortfall = Math.max(0, safeHarborAmount - projectedPaid);
  const penaltyExposure = shortfall > 0;

  return {
    safeHarborAmount,
    safeHarborBasis,
    priorYearSafeHarbor,
    currentYearSafeHarbor,
    hasPriorYear,
    withholding,
    paymentsMade,
    netRequired,
    perQuarter,
    quarterly,
    balanceDueAtFiling,
    penaltyExposure,
    shortfall,
  };
}

// Count applied strategies that resolve to a given status in a given scenario
// using the provided statusFor() resolver.
function countAppliedWithStatus(plan, scenarioId, targetStatus, statusFor) {
  let count = 0;
  for (const e of plan.entities || []) {
    for (const s of e.strategies || []) {
      if (s?.applied && statusFor(s, scenarioId) === targetStatus) count++;
    }
  }
  for (const s of plan.individual?.strategies || []) {
    if (s?.applied && statusFor(s, scenarioId) === targetStatus) count++;
  }
  return count;
}

// Walk every applied strategy in a plan and call cb(ref) for each.
function forEachAppliedStrategy(plan, cb) {
  for (const e of plan.entities || []) {
    for (const s of e.strategies || []) {
      if (s?.applied) cb(s);
    }
  }
  for (const s of plan.individual?.strategies || []) {
    if (s?.applied) cb(s);
  }
}

// Produce a shallow copy of the plan with a subset of strategy refs that
// pass a predicate. Used for status splits and per-strategy marginals.
function withStrategyFilter(plan, predicate) {
  const filterRefs = (refs) =>
    (refs || []).map((s) =>
      s.applied && !predicate(s) ? { ...s, applied: false } : s,
    );
  return {
    ...plan,
    entities: (plan.entities || []).map((e) => ({
      ...e,
      strategies: filterRefs(e.strategies),
    })),
    individual: {
      ...(plan.individual || {}),
      strategies: filterRefs(plan.individual?.strategies),
    },
  };
}

// ─── Plan factory ──────────────────────────────────────────────────────
export function newPlan({ id, clientName }) {
  return {
    id,
    clientName: clientName || "New Client",
    clientEmail: "",
    taxYear: 2026,
    state: "UT",
    createdAt: new Date().toISOString(),
    lifecycle: {
      stage: "prospect", // prospect | conversation | data_received | plan_built | presented | accepted | instituted | delivered
      notes: "",
    },
    // Three independent scenarios. Each strategy can be Yes / Maybe / Out per
    // scenario via stratRef.statusByScenario[scenarioId]. Scenarios can be
    // renamed by the partner (e.g. "Conservative" / "Balanced" / "Aggressive").
    scenarios: [
      { id: "s1", name: "Scenario 1" },
      { id: "s2", name: "Scenario 2" },
      { id: "s3", name: "Scenario 3" },
    ],
    // Multi-year projection settings
    incomeGrowth: 0.03, // 3% annual income growth assumption — partner can adjust
    projectionYears: 3,
    entities: [],
    individual: {
      filingStatus: "mfj",
      ownerNames: "",
      wages: 0,
      interest: 0,
      ordinaryDividends: 0,
      qualifiedDividends: 0,
      shortTermGains: 0,
      longTermGains: 0,
      otherIncome: 0,
      adjustments: 0,
      itemizedDeductions: 0,
      // Carryforwards from prior years (informational; planner applies via strategy)
      federalNOL: 0,
      stateNOL: 0,
      capitalLossCarryforward: 0,
      charitableCarryforward: 0,
      section163jCarryforward: 0,
      passiveLossCarryforward: 0,
      unusedFedCredits: 0,
      unusedFedCreditsNote: "",
      unusedFedCreditsItems: [],
      unusedStateCredits: 0,
      unusedStateCreditsNote: "",
      unusedStateCreditsItems: [],
      carryforwardNotes: "",
      // Tax-mechanic overrides for partner-controlled judgment items
      ltcgExcludedFromNIIT: 0, // dollars of LTCG that are NOT subject to NIIT (e.g. active-trade-or-business sale)
      qbiOverride: "", // blank = auto compute; otherwise planner-entered QBI deduction
      qbiOverrideNote: "",
      // AMT preferences/adjustments — planner enters net of ISO bargain element,
      // private activity bond interest, depreciation adjustments, AMT NOL, etc.
      amtPreferences: 0,
      amtPreferencesNote: "",
      // Prior-year facts for safe-harbor estimated tax (Form 2210)
      priorYearTotalTax: 0, // line 24 of prior-year 1040
      priorYearAGI: 0, // for the 110% rule when prior AGI > $150K
      expectedWithholding: 0, // W-2 + 1099 withholding expected this year
      estimatedPaymentsMade: 0, // running total of quarterly payments already sent
      // Dependents for §24 Child Tax Credit / Credit for Other Dependents
      qualifyingChildren: 0, // qualifying children under age 17 → $2K CTC each
      otherDependents: 0, // qualifying other dependents → $500 CFOD each
      // W-2 wages of the SE-income earner (Schedule SE Line 8a) — used to
      // coordinate the SS portion of SE tax against wages already collected.
      // Leave 0 to skip coordination (matches legacy behavior).
      seEarnerWages: 0,
      // Itemized deductions — Schedule A breakdown. Sum feeds itemizedDeductions.
      itemizedDeductionsItems: [],
      // Free-text descriptions for "Other" line items
      otherIncomeNote: "",
      // Stackable +/- line items for "Other Income" (preferred over the single numeric field)
      otherIncomeItems: [],
      // State-only adjustment at the 1040 level (e.g. state QBI addback)
      stateAdjustment: 0,
      stateAdjustmentNotes: "",
      // Free-form notes the planner keeps on the 1040
      notes: "",
      // Attachments: same shape as entity.attachments
      attachments: [],
      strategies: [],
    },
  };
}

export function newEntity({ id, name, type }) {
  return {
    id,
    name: name || "New Entity",
    type: type || "1120S",
    ownershipPct: 100,
    revenue: 0,
    expenses: 0,
    depreciation: 0,
    otherDeductions: 0,
    otherIncome: 0,
    // K-1 flow-through items separate from ordinary business income
    ltcg: 0, // entity-generated long-term capital gains (passes to owner at LTCG rate)
    stcg: 0, // entity-generated short-term capital gains (passes to owner at ordinary rate)
    passthroughCredits: 0, // legacy single-value (kept for backward compat)
    // Named pass-through credits — each carries a name so they flow to the 1040
    // with their identity intact ("R&D credit §41 from Acme S-Corp", etc.)
    // [{ id, name, amount }]. When set, this replaces the single passthroughCredits.
    passthroughCreditItems: [],
    // State-only adjustment: + = addback to state taxable income (e.g. NC bonus
    // depreciation addback), - = state-only deduction. Federal calc unaffected.
    stateAdjustment: 0,
    stateAdjustmentNotes: "",
    // Multi-state apportionment. If empty, the entity's income is sourced 100%
    // to the plan's home state. Otherwise, each row is { state, pct } and the
    // entity's taxable income distributes across states by pct (should sum to
    // 100). Each state's portion is taxed at that state's top rate; sum is the
    // owner-level state tax attributable to that entity.
    apportionment: [],
    pteCreditUsed: 0,
    pteCreditCarryforward: 0,
    federalNOL: 0, // federal NOL carryforward available
    stateNOL: 0, // state NOL carryforward available
    unusedFedCredits: 0, // legacy single total — kept for backward compat
    unusedStateCredits: 0, // legacy single total — kept for backward compat
    // Stackable named carryforward credits — preferred over the single totals.
    // [{ id, description, amount, sign }] — sign defaults to "+"
    unusedFedCreditsItems: [],
    unusedStateCreditsItems: [],
    carryforwardNotes: "",
    generalPartner: false,
    // Free-form notes the planner keeps on the entity (visible to all firm
    // users since plans are firm-wide).
    notes: "",
    // Attachments: { id, fileName, fileSize, fileType, storagePath, uploadedAt, uploadedBy }
    attachments: [],
    collapsed: false, // UI: whether the entity card is collapsed
    taxMethod: "accrual", // "cash" | "accrual"
    // Book-to-tax reconciliation: addbacks applied BEFORE cash/accrual.
    // Each line is a current-year dollar amount the partner adds back to
    // book accrual income (so book starts from Schedule L style, then we
    // arrive at adjusted accrual taxable income, then layer cash/accrual.)
    bookToTax: {
      bookDepreciation: 0,
      bookAmortization: 0,
      principalPayments: 0,
      mortgagePayments: 0,
      nonDeductibleTax: 0,
      other: 0, // legacy single value (kept for backward compat)
      otherNote: "", // legacy single note
      // Stackable additional addback line items — preferred over the single
      // "other" field. [{ id, description, amount }]
      otherItems: [],
      notes: "",
    },
    // QBI structural inputs — W-2 wages allocable to QBI activity and
    // UBIA of qualified property. Surfaced so partners can see the
    // wage/UBIA-based ceiling next to QBI on the 1040.
    qbiWages: 0,
    qbiUBIA: 0,
    cashAccrual: {
      priorAR: 0,
      currentAR: 0,
      yeAR: 0, // 12/31 year-end forecast — used by adjustment calc when entered
      priorAP: 0,
      currentAP: 0,
      yeAP: 0,
      priorAccruedLiab: 0,
      currentAccruedLiab: 0,
      yeAccruedLiab: 0,
      priorPrepaid: 0,
      currentPrepaid: 0,
      yePrepaid: 0,
      priorContractAsset: 0,
      currentContractAsset: 0,
      yeContractAsset: 0,
      priorContractLiab: 0,
      currentContractLiab: 0,
      yeContractLiab: 0,
      // Partner-defined additional cash↔accrual line items. Same column shape
      // as the standard 6 lines: prior · current · YE forecast. Direction
      // tells the adjustment which side: "asset" (adj = prior − ending) or
      // "liability" (adj = ending − prior).
      // [{ id, label, prior, current, ye, direction }]
      customLines: [],
      appliedAdjustmentOverride: "", // blank = use computed delta
    },
    otherDeductionsNote: "",
    // Stackable +/- line items for "Other Deductions" (preferred over the single numeric field)
    otherDeductionsItems: [],
    strategies: [],
  };
}

export const ENTITY_TYPE_OPTIONS = [
  { id: "1120S", label: "1120-S (S-Corp)" },
  { id: "1065", label: "1065 (Partnership)" },
  { id: "1120", label: "1120 (C-Corp)" },
  { id: "sched_c", label: "Schedule C (Sole Prop)" },
];

export const LIFECYCLE_STAGES = [
  { id: "prospect", label: "Prospect" },
  { id: "conversation", label: "Initial Conversation" },
  { id: "data_received", label: "Data Received" },
  { id: "plan_built", label: "Plan Built" },
  { id: "presented", label: "Presented to Client" },
  { id: "accepted", label: "Accepted" },
  { id: "instituted", label: "Instituted" },
  { id: "delivered", label: "Delivered" },
];

export default {
  computeEntity,
  computePlan,
  computePlanWithSavings,
  newPlan,
  newEntity,
  ENTITY_TYPE_OPTIONS,
  LIFECYCLE_STAGES,
};
